import ApiModel       from '@widesk-core/models/ApiModel';
import { ModelClass } from '@mathquis/modelx/lib/types/collection';
import React          from 'react';

/** Ce que la limite compte, au singulier et au pluriel : « 1 projet », « 5 projets ». */
export type FunctionalityUnit = {
	many: string;
	one: string;
};

export const UNLIMITED_VALUE = -1;

export const toLimit = (value?: string | number): number => {
	const parsed = Number(value);

	if (Number.isNaN(parsed)) return 0;

	return parsed === UNLIMITED_VALUE ? Infinity : parsed;
};

export type FunctionalityLimitEntry = {
	type: 'limit';
	model: () => ModelClass<ApiModel> | undefined;
	scope: string;
	unit?: FunctionalityUnit;
	// `never` en paramètre : chaque entrée déclare le type concret de son contexte
	filters: (context: never) => Record<string, unknown> | undefined;
};

export type FunctionalityEntry =
	| FunctionalityLimitEntry
	| { type: 'feature' }
	| { type: 'unconfigured'; unit?: FunctionalityUnit };

/**
 * Déclare une fonctionnalité de type limite : `model` est l'entité comptée côté API,
 * `scope` le périmètre dans lequel le backend applique la limite (voir les checks de
 * AccountSubscriptionFunctionalityCheckerService dans le service concerné), et `filters`
 * construit les filtres de comptage (typés par le `_tsFilters` du modèle) à partir de
 * l'entité de contexte — ou retourne `undefined` tant que les valeurs ne sont pas prêtes.
 * Le scope `account` est ambiant : son contexte (l'account courant) est fourni par la
 * config du moteur ; les autres scopes reçoivent l'entité passée à la prop `context` du gate.
 */
export function limit<M extends ApiModel, S extends string, C extends ApiModel>(
	model: () => ModelClass<M> | undefined,
	config: {
		scope: S;
		unit?: FunctionalityUnit;
		filters: (context: C) => ModelFilters<M> | undefined;
	},
) {
	return {
		type: 'limit' as const,
		model,
		scope: config.scope,
		unit: config.unit,
		filters: config.filters,
	};
}

/** Fonctionnalité de type on/off : activée si la valeur de l'abonnement vaut '1'. */
export const feature = { type: 'feature' } as const;

/**
 * Limite dont le comptage n'est pas encore branché côté front : à configurer au premier usage.
 * Elle s'affiche tout de même dans les écrans d'abonnement, d'où son unité.
 */
export const unconfigured = (config?: { unit?: FunctionalityUnit }) => ({
	type: 'unconfigured' as const,
	unit: config?.unit,
});

/** Références de type limite du dictionnaire D. */
export type FunctionalityLimitReferenceOf<D> = {
	[R in keyof D]: D[R] extends { type: 'limit' } ? R : never;
}[keyof D] & string;

/** Références de type on/off du dictionnaire D. */
export type FunctionalityFeatureReferenceOf<D> = {
	[R in keyof D]: D[R] extends { type: 'feature' } ? R : never;
}[keyof D] & string;

/** Références de type limite non comptée du dictionnaire D. */
export type FunctionalityUnconfiguredReferenceOf<D> = {
	[R in keyof D]: D[R] extends { type: 'unconfigured' } ? R : never;
}[keyof D] & string;

/** Entité de contexte attendue par une référence de type limite (le périmètre de comptage). */
export type FunctionalityContextOf<D, R extends FunctionalityLimitReferenceOf<D>> =
	D[R] extends { filters: (context: infer C) => unknown } ? C : never;

/**
 * Référence + contexte : les limites de scope `account` refusent la
 * prop `context` (le contexte est ambiant, fourni par la config), les autres l'exigent,
 * typée selon la signature `filters` de l'entrée.
 */
export type FunctionalityQueryOf<D, R extends FunctionalityLimitReferenceOf<D> = FunctionalityLimitReferenceOf<D>> = {
	[Ref in R]: { reference: Ref } & (D[Ref] extends { scope: infer S; filters: (context: infer C) => unknown }
		? (S extends 'account' ? { context?: never } : { context: C })
		: never);
}[R];

/** Ce qui bloque une action, tel que le gate le connaît au moment de l'afficher. */
export type BlockedFunctionality = {
	reference: string;
	limit?: number;
	scope?: string;
	usage?: number;
};

/** Configuration fournie par le projet consommateur. */
export type FunctionalityConfig<D> = {
	/** Le dictionnaire du projet (source de vérité des fonctionnalités). */
	functionalities: D;
	/** Account courant du projet (sa notion de session) ; null tant qu'aucun n'est sélectionné. */
	getAccount: () => (ApiModel & { urn: Urn }) | null;
	/** Valeurs portées par l'abonnement d'un compte (AccountSubscriptionFunctionality). */
	accountValueModel: ModelClass<ApiModel & { value?: string }>;
	/** Valeurs portées par une offre du catalogue (SubscriptionFunctionality). */
	planValueModel: ModelClass<ApiModel & { value?: string }>;
	/** Référentiel des fonctionnalités : porte le libellé traduit de chaque référence. */
	referentialModel: ModelClass<ApiModel & { reference: string; title?: string }>;
	/** Contenu de la bulle ouverte au clic sur une action bloquée ; sans lui, une simple infobulle. */
	renderBlockedDetails?: (blocked: BlockedFunctionality, close: () => void) => React.ReactNode;
};

/** Une fonctionnalité et la valeur que lui donne un abonnement ou un plan. */
export type FunctionalityValueOf<D> = {
	reference: keyof D & string;
	title: string;
	value: string;
};

/** Un jeu de valeurs, et s'il est déjà connu. */
export type FunctionalityValuesOf<D> = {
	isLoaded: boolean;
	values: FunctionalityValueOf<D>[];
};

/** Une limite dont la valeur change entre deux jeux. `usage` n'est connu que pour le scope account. */
export type FunctionalityChangeOf<D> = {
	from: number;
	reference: keyof D & string;
	title: string;
	to: number;
	usage?: number;
};

/** Ce que change le passage d'un jeu de valeurs à un autre. */
export type FunctionalityComparisonOf<D> = {
	/** Limites réduites en dessous de ce que le compte utilise déjà. */
	exceededLimits: FunctionalityChangeOf<D>[];
	gainedFeatures: FunctionalityValueOf<D>[];
	increasedLimits: FunctionalityChangeOf<D>[];
	isLoaded: boolean;
	keptFeatures: FunctionalityValueOf<D>[];
	keptLimits: FunctionalityChangeOf<D>[];
	lostFeatures: FunctionalityValueOf<D>[];
	reducedLimits: FunctionalityChangeOf<D>[];
};

export type CommonGateProps = {
	fallback?: 'tooltip' | 'hide' | 'alert' | 'disable';
	message?: string;
	fullWidth?: boolean;
	children: React.ReactNode;
};

export type FunctionalityGateProps<D> =
	| { [R in FunctionalityLimitReferenceOf<D>]: CommonGateProps & FunctionalityQueryOf<D, R> }[FunctionalityLimitReferenceOf<D>]
	| (CommonGateProps & { reference: FunctionalityFeatureReferenceOf<D> | FunctionalityUnconfiguredReferenceOf<D>; context?: never });

export type FunctionalityLockMarkProps<D> =
	| FunctionalityQueryOf<D>
	| { reference: FunctionalityFeatureReferenceOf<D> | FunctionalityUnconfiguredReferenceOf<D>; context?: never };

/** Signatures précises du store, dérivées du dictionnaire du projet. */
export type FunctionalityStoreOf<D> = {
	/** Une feature est-elle incluse dans l'abonnement ? `undefined` tant que ce n'est pas chargé. */
	isEnabled(reference: FunctionalityFeatureReferenceOf<D>): boolean | undefined;
	/** La valeur de l'abonnement rend-elle la fonctionnalité disponible ? `undefined` tant que ce n'est pas chargé. */
	isAvailable(reference: keyof D & string): boolean | undefined;
	/** Valeur maximale autorisée pour une limite. `undefined` tant que ce n'est pas chargé. */
	limit(reference: FunctionalityLimitReferenceOf<D>): number | undefined;
	/** Utilisation courante d'une limite (comptage côté API). `undefined` tant que ce n'est pas chargé. */
	usage(query: FunctionalityQueryOf<D>): number | undefined;
	/** La limite est-elle atteinte ? `undefined` tant que limite et utilisation ne sont pas chargées. */
	isLimitReached(query: FunctionalityQueryOf<D>): boolean | undefined;
	/** Version impérative de `isEnabled` : attend le chargement (et l'account si nécessaire). */
	whenEnabled(reference: FunctionalityFeatureReferenceOf<D>): Promise<boolean>;
	/** Version impérative de `isAvailable` : attend le chargement. */
	whenAvailable(reference: keyof D & string): Promise<boolean>;
	/** Version impérative de `isLimitReached` : attend le chargement (et l'account si nécessaire). */
	whenLimitReached(query: FunctionalityQueryOf<D>): Promise<boolean>;
	/** Relance le chargement de la valeur d'abonnement (limite max ou activation d'une feature). */
	refreshSubscriptionValue(reference: keyof D & string): void;
	/** Relance le comptage d'une limite (à appeler quand l'utilisation a pu changer, ex : montage d'un gate). */
	refreshUsage(query: FunctionalityQueryOf<D>): void;
	/** Relance tous les comptages déjà lus d'une limite, quel que soit leur contexte : après une création ou une suppression. */
	refreshUsagesOf(reference: FunctionalityLimitReferenceOf<D>): void;
	/** Le comptage d'une limite est-il possible (compte, modèle et filtres prêts) ? */
	canCount(query: FunctionalityQueryOf<D>): boolean;

	/** La fonctionnalité est-elle de type on/off ? */
	isFeature(reference: keyof D & string): boolean;
	/** Périmètre d'application d'une limite (`undefined` pour une feature). */
	scopeOf(reference: keyof D & string): string | undefined;
	/** Ce que compte une limite, au singulier et au pluriel (`undefined` si le projet ne l'a pas déclaré). */
	unitOf(reference: keyof D & string): FunctionalityUnit | undefined;
	/** Libellé traduit d'une fonctionnalité, lu dans le référentiel (`undefined` tant qu'il n'est pas chargé). */
	titleOf(reference: keyof D & string): string | undefined;
	/** Cette valeur rend-elle la fonctionnalité disponible ? Feature activée, ou limite non nulle. */
	isIncluded(reference: keyof D & string, value: string): boolean;

	/** Valeurs portées par l'abonnement d'un compte. Lecture pure : charger avant avec `loadAccountValues`. */
	accountValues(accountSubscriptionId: id): FunctionalityValuesOf<D>;
	/** Valeurs portées par un plan du catalogue. Lecture pure : charger avant avec `loadPlanValues`. */
	planValues(planId: id): FunctionalityValuesOf<D>;
	/** Ce que change le passage d'un jeu de valeurs à un autre, utilisation courante comprise. */
	compare(from: FunctionalityValuesOf<D>, to: FunctionalityValuesOf<D>): FunctionalityComparisonOf<D>;

	/** Charge les valeurs d'un abonnement (et le référentiel). À appeler hors render. */
	loadAccountValues(accountSubscriptionId: id): void;
	/** Relit les valeurs d'un abonnement : après un changement de plan, ce ne sont plus les mêmes. */
	refreshAccountValues(accountSubscriptionId: id): void;
	/** Charge le référentiel des fonctionnalités, dans la langue du moment. À appeler hors render. */
	loadReferential(): void;
	/** Charge les valeurs de tous les plans (et le référentiel). À appeler hors render. */
	loadPlanValues(): void;
	/** Relance le comptage de toutes les limites de scope account. À appeler hors render. */
	loadAccountUsages(): void;
};
